import { Component, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CheckboxComponent } from 'src/app/shared/ui-common/checkbox/checkbox.component';
import { SurveyFeedbackRecipients, HrRecipient, ManagerRecipient, FeedbackFormValue } from '../../data/survey-feedback.model';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { Account } from 'src/app/shared/data/models/account.model';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';

@Component({
  selector: 'app-survey-feedback',
  templateUrl: './survey-feedback.component.html',
  styleUrls: ['./survey-feedback.component.scss'],
  imports: [
    CommonModule,
    CheckboxComponent,
    ReactiveFormsModule,
    ButtonComponent,
    TranslatePipe
  ]
})
export class SurveyFeedbackComponent implements OnChanges {
  @Input() recipients!: SurveyFeedbackRecipients;
  @Input() account!: Account;
  @Input() disabled = false;
  @Output() feedbackSurveyFinished = new EventEmitter<FeedbackFormValue>();
  @Output() skipFeedback = new EventEmitter<void>();

  isDropdownOpen = signal(false);
  isRecipientsDropdownOpen = signal(false);
  isMessageSent = signal(false);
  senders = ['Anonymous'];
  feedbackForm: FormGroup;
  myFullName = '';

  get senderChecked(): boolean {
    return this.isDropdownOpen() ? this.senders[0] === this.selectedSender : !!this.selectedSender;
  }

  get selectedRecipientsNames(): string {
    const managerRecipients = this.feedbackForm.get('managerRecipients')?.value;
    const hrRecipients = this.feedbackForm.get('hrRecipients')?.value;
    const allRecipients = [...managerRecipients, ...hrRecipients].filter(r => r != null);

    return allRecipients?.map((r: HrRecipient | ManagerRecipient) => r.fullName).join(', ') || '';
  }

  get selectedSender(): string {
    return this.feedbackForm.get('sender')?.value || 'Anonymous';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recipients']?.currentValue) {
      this.feedbackForm.patchValue({
        managerRecipients: [this.recipients.managerRecipients[0]],
        hrRecipients: []
      });
    }

    if (changes['account']?.currentValue) {
      this.myFullName = `${this.account.firstname} ${this.account.lastname}`;
      this.senders = [...this.senders.filter(s => s !== this.myFullName), this.myFullName];
    }
  }

  constructor(private fb: FormBuilder) {
    this.feedbackForm = this.fb.group({
      sender: ['Anonymous'],
      isAnonymous: [true],
      managerRecipients: [[]],
      hrRecipients: [[]],
      message: ['', { validators: [Validators.required, Validators.minLength(5)] }]
    }, { validators: this.recipientsValidator });

    this.feedbackForm.get('sender')?.valueChanges.subscribe(value => {
      this.feedbackForm.patchValue({ isAnonymous: value === 'Anonymous' }, { emitEvent: false });
    });
  }

  toggleDropdown(): void {
    if (this.isRecipientsDropdownOpen()) {
      this.isRecipientsDropdownOpen.set(false);
    }
    this.isDropdownOpen.update(value => !value);
  }

  toggleRecipientsDropdown(): void {
    if (this.isDropdownOpen()) {
      this.isDropdownOpen.set(false);
    }
    this.isRecipientsDropdownOpen.update(value => !value);
  }

  selectSender(): void {
    const newSender = this.selectedSender === 'Anonymous' ? this.myFullName : 'Anonymous';

    this.feedbackForm.patchValue({
      sender: newSender,
      isAnonymous: newSender === 'Anonymous'
    });

    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  toggleRecipient(recipient: HrRecipient | ManagerRecipient): void {
    const isManager = 'noOfEmployees' in recipient;
    const controlName = isManager ? 'managerRecipients' : 'hrRecipients';
    const currentRecipients = this.feedbackForm.get(controlName)?.value || [];
    const isSelected = currentRecipients.some((r: HrRecipient | ManagerRecipient) => r.userId === recipient.userId);

    const newRecipients = isSelected
      ? currentRecipients.filter((r: HrRecipient | ManagerRecipient) => r.userId !== recipient.userId)
      : [...currentRecipients, recipient];

    this.feedbackForm.patchValue({ [controlName]: newRecipients });
  }

  isRecipientSelected(recipient: HrRecipient | ManagerRecipient): boolean {
    const isManager = 'noOfEmployees' in recipient;
    const controlName = isManager ? 'managerRecipients' : 'hrRecipients';

    return this.feedbackForm
      .get(controlName)?.value?.some((r: HrRecipient | ManagerRecipient) => r.userId === recipient.userId);
  }

  onSubmit(): void {
    if (this.feedbackForm.valid) {
      this.isMessageSent.set(true);

      if (!this.feedbackForm.get('managerRecipients')?.value[0]?.userId) {
        this.feedbackForm.patchValue({ managerRecipients: [] });
      }

      setTimeout(() => {
        this.feedbackSurveyFinished.emit(this.feedbackForm.value);
      }, 2000);
    }
  }

  onSkipFeedback(): void {
    if (this.disabled) {
      return;
    }

    this.skipFeedback.emit();
  }

  private recipientsValidator(control: AbstractControl): ValidationErrors | null {
    const managerRecipients = control.get('managerRecipients')?.value || [];
    const hrRecipients = control.get('hrRecipients')?.value || [];

    if (managerRecipients.length === 0 && hrRecipients.length === 0) {
      return { noRecipientsSelected: true };
    }

    return null;
  }
}
